import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import {
  Button,
  Col,
  Input,
  InputGroup,
  Row,
  Spinner,
  InputGroupAddon,
} from 'reactstrap';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect } from 'react-redux';
import {
  setCheckout,
  setCart,
  setOrder,
  setOrderHash,
  setCheckoutBackup,
} from '../../../../redux/store/store.actions';
import {
  setIsCheckoutModalOpen,
  setIsOrderInfoModalOpen,
  setIsOrderNotificationsModalOpen,
  setIsPickupStatusModalOpen,
  setIsSignUpNoticeModalOpen,
  setOrderInfoModalData,
} from '../../../../redux/modal/modal.actions';
import PropTypes from 'prop-types';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import SkeletonLoader from '../../lazyload/skeleton-loader/skeleton-loader.component';
import { customMessageNotification } from '../../../../utility/notification/notification.service';
import { confirmPaymentByType, updateOrder } from './checkout.service';
import {
  fetchOnAuthenticated,
  fetchOrders,
  fetchPaymentMethods,
  setPickupOrder,
  fetchMe,
} from '../../../../redux/me/me.actions';
import {
  createHash,
  getPaymentMethodDetails,
  getLocalizedName,
} from '../../../../utility/util/util.service';
import classnames from 'classnames';
import * as Icon from 'react-feather';
import LabeledToggle from '../../form/LabeledToggle';
import DefaultAlert from '../../alerts/default-alert/default-alert.component';
import { config } from '../../../../configs/app.config';
import {
  CHECKOUT_METHOD_PAY_AND_GO,
  ORDER_TYPE_CATERING,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_INHOUSE,
  ORDER_TYPE_PICKUP,
  ORDER_TYPE_SELF_SERVICE,
} from '../../../../utility/constants';
import TermsAndConditionsText from '../../helpers/agreementText.component';
import TagManager from 'react-gtm-module/dist/TagManager';
import { useHistory } from 'react-router-dom';
import { useRef } from 'react';
import OptionsList from '../../selectors/options-list/options-list.component';
import { AvForm } from 'availity-reactstrap-validation';
import SaveSubmitButton from '../../form/buttons/SaveSubmitButton';

const CheckoutModal = ({
  isOpen,
  cart,
  store,
  checkout,
  checkoutBackup,
  me,
  order,
  orderHash,
  paymentMethods,
  table,
  kiosk,

  setIsCheckoutModalOpen,
  setCheckout,
  setCheckoutBackup,
  setCart,
  setOrder,
  setOrderHash,
  fetchOrders,
  setIsOrderInfoModalOpen,
  setOrderInfoModalData,
  fetchPaymentMethods,
  setIsSignUpNoticeModalOpen,
  setIsOrderNotificationsModalOpen,
  fetchMe,
}) => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();

  const formRef = useRef(null);
  const [chosenPaymentMethod, setChosenPaymentMethod] = useState(null);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [isOrderUpdating, setIsOrderUpdating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discountCode, setDiscountCode] = useState(null);
  const [discountCodeInvalid, setDiscountCodeInvalid] = useState(false);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const hasSavedPaymentMethods = !!paymentMethods?.length;
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);

  // reset states on modal close; sync the discount field with the order/checkout
  // when it opens so an already-applied code shows (and can be removed)
  useEffect(() => {
    if (!isOpen) {
      setIsPaymentElementReady(false);
      setIsSubmitting(false);
      setIsOrderUpdating(false);
      setChosenPaymentMethod(null);
      setIsApplyingDiscount(false);
      setDiscountCodeInvalid(false);
    } else {
      setDiscountCode(checkout?.discountCode ?? null);
      setDiscountCodeInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // pre-select payment method
  useEffect(() => {
    if (isOpen && !chosenPaymentMethod) {
      let paymentMethod = null;
      const defaultPaymentMethod = paymentMethods?.find(
        ({ isDefault }) => isDefault,
      );

      if (defaultPaymentMethod?.id) {
        paymentMethod = defaultPaymentMethod;
      } else if (hasSavedPaymentMethods) {
        paymentMethod = paymentMethods[0];
      } else {
        paymentMethod = 'ADD_NEW';
      }

      console.debug(`Pre-selected paymentMethod (id=${paymentMethod})`);
      setChosenPaymentMethod(paymentMethod);
    }
  }, [isOpen, paymentMethods]);

  // updates the order if the orderHash changes
  useEffect(() => {
    if (isOpen && order && !isSubmitting && elements) {
      const hash = createHash({ cart: cart, checkout: checkout });

      // update order if hashes are not equal (something changed)
      if (hash !== orderHash) {
        setIsOrderUpdating(true);
        updateOrder(
          me,
          order,
          checkout,
          cart,
          store,
          table,
          kiosk,
          setOrder,
          setOrderHash,
          intl,
          fetchMe,
        )
          .then(() => elements.fetchUpdates())
          .then(({ error }) => {
            setIsOrderUpdating(false);
            setIsApplyingDiscount(false);
            setDiscountCodeInvalid(false);

            TagManager.dataLayer({
              dataLayer: {
                event: 'InitiateCheckout',
                items: cart.map((item) => ({
                  itemDataId: item.id,
                  itemName: getLocalizedName(item, intl.locale),
                  itemPrice: item?.price,
                })),
              },
            });

            if (error) throw new Error(error.message);
          })
          .catch((err) => {
            console.error(err);
            setIsOrderUpdating(false);
            setIsApplyingDiscount(false);

            // A rejected discount code must not tear down the whole checkout:
            // keep the modal open, flag the field inline, and let the shopper
            // fix or remove the code (the toast is shown by checkout.service).
            const errorCode = err?.response?.data?.error;
            if (
              typeof errorCode === 'string' &&
              errorCode.startsWith('DISCOUNT_CODE')
            ) {
              setDiscountCodeInvalid(true);
            } else {
              setIsCheckoutModalOpen(false);
            }
          });
      }
    }
  }, [
    cart,
    checkout,
    elements,
    isOpen,
    isSubmitting,
    me,
    order,
    orderHash,
    setIsCheckoutModalOpen,
    setOrder,
    setOrderHash,
    store,
  ]);

  // Apply the entered code by pushing it into the checkout state, which the
  // update effect above sends to the backend (it recomputes the discount).
  const handleApplyDiscount = () => {
    if (isApplyingDiscount || isOrderUpdating) return;
    setDiscountCodeInvalid(false);
    setIsApplyingDiscount(true);
    setCheckout({ discountCode: discountCode || '' });
  };

  // Remove an applied code. An empty string is an explicit "clear" for the
  // backend (it resets the discount to 0).
  const handleRemoveDiscount = () => {
    if (isApplyingDiscount || isOrderUpdating) return;
    setDiscountCode('');
    setDiscountCodeInvalid(false);
    setIsApplyingDiscount(true);
    setCheckout({ discountCode: '' });
  };

  const handleSubmit = async () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'InitiatePayment',
        orderId: order?.id,
      },
    });

    /*if (event) event.preventDefault();*/
    history.replace({ search: null });

    if (stripe && elements && chosenPaymentMethod) {
      setIsSubmitting(true);

      // save the checkout session, we need it after returning back from redirect
      localStorage.setItem(
        'prevCheckoutSession',
        JSON.stringify({ checkout, cart, order }),
      );

      // set return url to open the order-info-modal after return
      const returnUrl = table
        ? window.location.origin + `/table/${table?.slug}?orderId=${order?.id}`
        : `${window.location.origin + window.location.pathname}?orderId=${
            order?.id
          }`;

      const { error } =
        chosenPaymentMethod === 'ADD_NEW'
          ? await stripe.confirmPayment({
              elements: elements,
              redirect: 'if_required',
              confirmParams: { return_url: returnUrl },
            })
          : await confirmPaymentByType(stripe, chosenPaymentMethod.type)(
              order.payment.clientSecret,
              {
                payment_method: chosenPaymentMethod?.id,
                return_url: returnUrl,
              },
            );

      if (error) {
        customMessageNotification('warn', error.message);
        setIsSubmitting(false);
      } else {
        // V2: No explicit submit needed for Stripe online payments.
        // The Stripe webhook (payment_intent.succeeded) automatically updates
        // the payment status and order status (PENDING → PAID → PREPARING).

        setOrderInfoModalData({ order, isInitial: true });
        setIsOrderInfoModalOpen(true);

        if (checkout.paymentMethodFutureUsage) fetchPaymentMethods();

        // restore the checkout session from before pay&go order
        if (
          checkoutBackup &&
          checkout.checkoutMethod === CHECKOUT_METHOD_PAY_AND_GO
        ) {
          console.debug('Restoring checkout backup...');
          setCheckout(checkoutBackup);
          setCheckoutBackup(null);
        } else {
          setCheckout(null);
        }

        setCart(null);
        setOrder(null);
        setOrderHash(null);
        setIsSubmitting(false);
      }
    }
  };

  // Discount codes can be disabled per order type via OrderConfig.allowDiscountCode.
  const orderConfigKey = {
    [ORDER_TYPE_SELF_SERVICE]: 'selfServiceOrderConfig',
    [ORDER_TYPE_PICKUP]: 'pickupOrderConfig',
    [ORDER_TYPE_INHOUSE]: 'inhouseOrderConfig',
    [ORDER_TYPE_DELIVERY]: 'deliveryOrderConfig',
    [ORDER_TYPE_CATERING]: 'cateringOrderConfig',
  }[order?.type];
  const activeOrderConfig = orderConfigKey ? store?.[orderConfigKey] : null;
  const allowDiscountCode = activeOrderConfig?.allowDiscountCode !== false;

  return (
    <ModalSheet
      footer={
        <div className="align-content-center">
          <div className="w-100 ">
            <TermsAndConditionsText order={order} />
            <SaveSubmitButton
              data-testid="placeOrderButton"
              color="primary"
              className="w-100 py-2 mt-1"
              isSubmitting={isSubmitting}
              disabled={
                isSubmitting ||
                isOrderUpdating ||
                isApplyingDiscount ||
                discountCodeInvalid ||
                !(stripe && elements && chosenPaymentMethod) ||
                (chosenPaymentMethod === 'ADD_NEW' &&
                  (!isPaymentElementReady || !isPaymentComplete))
              }
              messageId={
                checkout?.checkoutMethod === CHECKOUT_METHOD_PAY_AND_GO
                  ? 'checkoutMethodPayAndGoButtonLabel'
                  : 'checkoutButtonLabel'
              }
              messageValues={{
                total: intl.formatNumber(order?.total, {
                  format: 'priceCurrency',
                }),
              }}
              onClick={() => formRef?.current.handleSubmit()} // this button is outside the AvForm. So trigger submit programmatically.
            />
          </div>
        </div>
      }
      isOpen={!!isOpen}
      closeModal={() => {
        // empty cart if it was a PAY_AND_GO order and the modal was closed
        if (checkout.checkoutMethod === CHECKOUT_METHOD_PAY_AND_GO) {
          setOrder(null);
          setCart(null);
        }
        setIsCheckoutModalOpen(false);
      }}
      title={intl.formatMessage({ id: 'checkoutTitle' })}
      disableEnterToSubmit={true}
    >
      <AvForm ref={formRef} onValidSubmit={handleSubmit} className="px-1">
        {['dev', 'demo'].includes(config.env) && (
          <DefaultAlert
            color="warning"
            msg={intl.formatMessage({ id: 'demoModeCheckoutPaymentAlert' })}
          />
        )}
        <div>
          {/* List saved PaymentMethods */}
          {hasSavedPaymentMethods && (
            <>
              <span className="font-small-3">
                <FormattedMessage id="paymentMethodsTitle" />
              </span>
              <OptionsList
                isMulti={false}
                options={paymentMethods}
                values={chosenPaymentMethod && [chosenPaymentMethod.id]}
                getOptionKey={({ id }) => id}
                getOptionPrefix={(paymentMethod) => {
                  const { name, iconSrc } = getPaymentMethodDetails(
                    paymentMethod,
                  );
                  return (
                    <div style={{ width: '55px' }}>
                      <img
                        alt={name + ' Logo'}
                        className="thumbnail-h35"
                        src={iconSrc}
                      />
                    </div>
                  );
                }}
                getOptionValue={(paymentMethod) => (
                  <div className="text-right">
                    {getPaymentMethodDetails(paymentMethod)?.['suffix']}
                  </div>
                )}
                onChange={(key) => {
                  const paymentMethod = paymentMethods.find(
                    ({ id }) => id === key,
                  );
                  setChosenPaymentMethod(paymentMethod);
                }}
                createNewOptionMessageId={
                  chosenPaymentMethod !== 'ADD_NEW' && 'addNewPaymentMethod' // hack to hide the new option row
                }
                handleCreateNewOption={() => setChosenPaymentMethod('ADD_NEW')}
                propertyName="paymentMethodId"
              />
            </>
          )}

          {/* PaymentElement and it's skeleton loader */}
          {chosenPaymentMethod === 'ADD_NEW' && (
            <div className={classnames({ 'mt-1': hasSavedPaymentMethods })}>
              {(!isPaymentElementReady || isOrderUpdating) && (
                <Row>
                  <Col xs={4} className="pr-25 pb-2">
                    <SkeletonLoader height="72px" />
                  </Col>
                  <Col xs={4} className="px-25 pb-2">
                    <SkeletonLoader height="72px" />
                  </Col>
                  <Col xs={4} className="pl-25 pb-2">
                    <SkeletonLoader height="72px" />
                  </Col>
                  <Col xs={12}>
                    <SkeletonLoader height="38px" />
                  </Col>
                  <Col xs={6} className="pr-25 py-2">
                    <SkeletonLoader height="38px" />
                  </Col>
                  <Col xs={6} className="pl-25 py-2">
                    <SkeletonLoader height="38px" />
                  </Col>
                  <Col xs={12}>
                    <SkeletonLoader height="38px" />
                  </Col>
                  {!!checkout?.paymentMethodFutureUsage && (
                    <Col xs={12} className="pt-2">
                      <SkeletonLoader count="2" height="12px" />
                      <SkeletonLoader height="12px" width="50%" />
                    </Col>
                  )}
                </Row>
              )}
              <div hidden={!isPaymentElementReady || isOrderUpdating}>
                {['dev', 'demo'].includes(config.env) && (
                  <DefaultAlert
                    color="info"
                    msg={intl.formatMessage({
                      id: 'demoModeCheckoutPaymentInfo',
                    })}
                  />
                )}
                <PaymentElement
                  onReady={() => setIsPaymentElementReady(true)}
                  onChange={(event) => setIsPaymentComplete(event.complete)}
                  options={{
                    business: { name: store?.name + ' (via servier.bar)' },
                    readOnly: isSubmitting,
                    paymentMethodOrder: [
                      'apple_pay',
                      'google_pay',
                      'card',
                      'twint',
                      'paypal',
                      'sepa_debit',
                      'klarna',
                    ],
                  }}
                />
              </div>

              {!kiosk && (
                <LabeledToggle
                  disabled={
                    !isPaymentElementReady || isOrderUpdating || isSubmitting
                  }
                  checked={!!checkout?.paymentMethodFutureUsage}
                  onClick={(target) =>
                    setCheckout({
                      paymentMethodFutureUsage:
                        !kiosk && target.checked ? 'on_session' : null,
                    })
                  }
                  wrapperClassName="d-flex align-content-center justify-content-between mt-2"
                  headingMessageId="savePaymentMethodTitle"
                  headingClassName="d-block dark"
                />
              )}
            </div>
          )}
          {allowDiscountCode && (
            <>
              <Row className="mt-1">
                <Col className="d-flex" xs={4} sm={4} md={6}>
                  <span className="dark align-self-center">
                    <FormattedMessage id="discountCodeCheckoutLabel" />
                  </span>
                </Col>
                <Col xs={8} sm={8} md={6}>
                  <InputGroup>
                    <Input
                      type="text"
                      rows="1"
                      className="bg-transparent font-small-3 line-height-15 "
                      placeholder={intl.formatMessage({
                        id: 'discountCodePlaceHolder',
                      })}
                      value={discountCode || ''}
                      disabled={isApplyingDiscount}
                      invalid={discountCodeInvalid}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyDiscount();
                        }
                      }}
                      onChange={(e) => {
                        e.preventDefault();
                        setDiscountCode(e.target.value);
                        if (discountCodeInvalid) setDiscountCodeInvalid(false);
                      }}
                    />

                    <InputGroupAddon addonType="append">
                      {discountCode && !isApplyingDiscount && (
                        <Button
                          outline
                          color="dark"
                          size="sm"
                          aria-label={intl.formatMessage({
                            id: 'removeDiscountCode',
                          })}
                          onClick={handleRemoveDiscount}
                        >
                          <Icon.X size={15} />
                        </Button>
                      )}
                      <Button
                        color="primary"
                        size="sm"
                        disabled={isApplyingDiscount}
                        onClick={handleApplyDiscount}
                      >
                        {isApplyingDiscount ? (
                          <Spinner size="sm" />
                        ) : (
                          <Icon.Check size={15} />
                        )}
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </Col>
              </Row>
              {isApplyingDiscount && (
                <Row className="mt-1">
                  <Col className="d-flex align-items-center" xs={12}>
                    <Spinner size="sm" className="mr-1" />
                    <small className="dark">
                      <FormattedMessage id="applyingDiscountCode" />
                    </small>
                  </Col>
                </Row>
              )}
              {!isApplyingDiscount && discountCodeInvalid && (
                <Row className="mt-1">
                  <Col xs={12}>
                    <small className="text-danger">
                      <FormattedMessage id="discountCodeError" />
                    </small>
                  </Col>
                </Row>
              )}
              {!isApplyingDiscount &&
                !discountCodeInvalid &&
                order?.discount > 0 && (
                  <Row className="mt-1">
                    <Col className="d-flex justify-content-between" xs={12}>
                      <small className="dark">
                        <FormattedMessage
                          id="discountCodeValueLabel"
                          values={{
                            discountCode:
                              order?.discountCode?.code || discountCode || '',
                          }}
                        />
                      </small>
                      <small className="text-success">
                        {'-'}
                        <FormattedNumber
                          value={order.discount}
                          format="priceCurrency"
                        />
                      </small>
                    </Col>
                  </Row>
                )}
            </>
          )}
        </div>
      </AvForm>
    </ModalSheet>
  );
};

CheckoutModal.propTypes = {
  isOpen: PropTypes.bool,
  cart: PropTypes.array,
  store: PropTypes.object,
  table: PropTypes.object,
  checkout: PropTypes.object,
  order: PropTypes.object,
  orderHash: PropTypes.string,
  me: PropTypes.object,
  setIsCheckoutModalOpen: PropTypes.func,
  setCheckout: PropTypes.func,
  setCart: PropTypes.func,
  setOrder: PropTypes.func,
  setOrderHash: PropTypes.func,
  setIsOrderInfoModalOpen: PropTypes.func,
  setOrderInfoModalData: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isCheckoutModalOpen,
  cart: state.store.cart,
  store: state.store.store,
  table: state.store.table,
  checkout: state.store.checkout,
  checkoutBackup: state.store.checkoutBackup,
  order: state.store.order,
  orderHash: state.store.orderHash,
  paymentMethods: state.me.paymentMethods,
  me: state.me.me,
  kiosk: state.store.kiosk,
});

export default connect(mapStateToProps, {
  setIsCheckoutModalOpen,
  setCheckout,
  setCart,
  setPickupOrder,
  setIsPickupStatusModalOpen,
  fetchOnAuthenticated,
  setOrder,
  setOrderHash,
  fetchOrders,
  setIsOrderInfoModalOpen,
  setOrderInfoModalData,
  fetchPaymentMethods,
  setIsSignUpNoticeModalOpen,
  fetchMe,
  setCheckoutBackup,
  setIsOrderNotificationsModalOpen,
})(CheckoutModal);
